import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import methodModel from "../../methods/methods";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import loader from "../../methods/loader";
import { Chart, registerables } from "chart.js";
import { AiOutlinePieChart } from "react-icons/ai";
import {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiLock,
  FiFile,
  FiCreditCard,
  FiAlertCircle,
  FiDollarSign,
} from "react-icons/fi";

// Register ChartJS elements
Chart.register(...registerables);

// Reusable SVG file icon used in original code
const FileIcon = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none" width="26" height="26">
    <g clipPath="url(#clip0_178_332)">
      <path d="M22.4218 6.24509L16.3973 0.220651C16.2567 0.0799764 16.0648 0 15.8648 0H5.32172C4.07599 0 3.0625 1.01349 3.0625 2.25922V23.4457C3.0625 24.6914 4.07599 25.7049 5.32172 25.7049H20.3832C21.6289 25.7049 22.6424 24.6914 22.6424 23.4457V6.77766C22.6424 6.57233 22.5562 6.37949 22.4218 6.24509ZM16.6178 2.57114L20.0713 6.02459H17.3709C16.9557 6.02459 16.6178 5.68676 16.6178 5.27152V2.57114ZM20.3832 24.1988H5.32172C4.90648 24.1988 4.56865 23.8609 4.56865 23.4457V2.25922C4.56865 1.84398 4.90648 1.50615 5.32172 1.50615H15.1117V5.27152C15.1117 6.51725 16.1252 7.53074 17.3709 7.53074H21.1363V23.4457C21.1363 23.8609 20.7984 24.1988 20.3832 24.1988Z" fill={color} />
      <path d="M17.37 10.6434H8.33315C7.91725 10.6434 7.58008 10.9806 7.58008 11.3965C7.58008 11.8124 7.91725 12.1496 8.33315 12.1496H17.37C17.7859 12.1496 18.1231 11.8124 18.1231 11.3965C18.1231 10.9806 17.7859 10.6434 17.37 10.6434Z" fill={color} />
      <path d="M17.37 13.6558H8.33315C7.91725 13.6558 7.58008 13.9929 7.58008 14.4088C7.58008 14.8247 7.91725 15.1619 8.33315 15.1619H17.37C17.7859 15.1619 18.1231 14.8247 18.1231 14.4088C18.1231 13.9929 17.7859 13.6558 17.37 13.6558Z" fill={color} />
      <path d="M17.37 16.6681H8.33315C7.91725 16.6681 7.58008 17.0053 7.58008 17.4212C7.58008 17.8371 7.91725 18.1742 8.33315 18.1742H17.37C17.7859 18.1742 18.1231 17.8371 18.1231 17.4212C18.1231 17.0053 17.7859 16.6681 17.37 16.6681Z" fill={color} />
      <path d="M14.3577 19.6803H8.33315C7.91725 19.6803 7.58008 20.0175 7.58008 20.4334C7.58008 20.8493 7.91725 21.1864 8.33315 21.1864H14.3577C14.7736 21.1864 15.1108 20.8493 15.1108 20.4334C15.1108 20.0175 14.7736 19.6803 14.3577 19.6803Z" fill={color} />
    </g>
    <defs>
      <clipPath id="clip0_178_332">
        <rect width="25.7049" height="25.7049" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// ─── Redesigned StatCard with animations (Tailwind only) ───────────────────
const StatCard = ({ title, subtitle, desc, value, Icon, iconColor, iconBg, onClick, delay = 0 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
      return;
    }
    const timer = setTimeout(() => {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.45s ease, transform 0.45s ease" }}
      className="group relative bg-white border border-gray-100 rounded-2xl p-4 flex flex-col overflow-hidden cursor-pointer hover:!-translate-y-1 hover:border-gray-200 hover:shadow-lg active:scale-[0.98] transition-all duration-300"
    >
      {/* shine sweep */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"
      />

    <div className="flex gap-2 justify-between items-center mb-4">
      <div
        style={{ background: iconBg }}
        className="w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6"
      >
        {Icon && <Icon size={20} color={iconColor} />}
      </div>
                   <span className="text-lg sm:text-[22px] font-medium text-gray-800 transition-transform duration-200 group-hover:scale-105">
          {value}
        </span>
        </div>

      {/* subtitle */}
      {/* <p className="text-xs uppercase tracking-widest text-gray-600 mb-0.5">{subtitle}</p> */}

      {/* title */}
      <p className="text-md sm:text-lg font-medium text-gray-800 mb-1.5">{title}</p>

      {/* description */}
      {/* <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{desc}</p> */}
    </div>
  );
};

// ─── Animated Section Label ────────────────────────────────────────────────
const SectionLabel = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { el.style.opacity = "1"; el.style.transform = "translateY(0)"; return; }
    const t = setTimeout(() => { el.style.opacity = "1"; el.style.transform = "translateY(0)"; }, delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      ref={ref}
      style={{ opacity: 0, transform: "translateY(8px)", transition: "opacity 0.4s ease, transform 0.4s ease" }}
      className="flex items-center gap-3 mb-5"
    >
      <h2 className="text-xs font-semibold tracking-widest uppercase text-gray-500 whitespace-nowrap">
        {children}
      </h2>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
};

// ─── Interactive Chart Component (unchanged) ──────────────────────────────
const InteractiveChart = ({ title, type, graphData, year, setYear, icon }) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartType, setChartType] = useState("bar");

  useEffect(() => {
    if (!canvasRef.current || !graphData) return;
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    const ctx = canvasRef.current.getContext("2d");
    const labels = graphData.labels || [];
    const isPieOrDoughnut = chartType === "pie" || chartType === "doughnut";

    let chartData;
    let options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 800, easing: "easeOutQuart" },
      plugins: {
        legend: {
          position: "top",
          labels: {
            boxWidth: 8, usePointStyle: true, pointStyle: "circle",
            padding: 18, color: "#4b5563",
            font: { family: "Inter", weight: "600", size: 12 },
          },
        },
        tooltip: {
          backgroundColor: "#111827", titleColor: "#ffffff", bodyColor: "#f3f4f6",
          padding: 12, cornerRadius: 8,
          titleFont: { family: "Inter", size: 13, weight: "700" },
          bodyFont: { family: "Inter", size: 12 },
          callbacks: {
            label: function (context) {
              let lbl = context.dataset.label || "";
              if (lbl) lbl += ": ";
              const val = context.parsed.y !== undefined ? context.parsed.y : context.parsed;
              if (val !== null) lbl += "$" + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return lbl;
            },
          },
        },
      },
    };

    if (isPieOrDoughnut) {
      if (type === "invoice") {
        chartData = {
          labels: ["Paid Amount", "Due Amount"],
          datasets: [{ data: [graphData.paidAmount || 0, graphData.dueAmount || 0], backgroundColor: ["rgba(16,185,129,0.75)", "rgba(239,68,68,0.75)"], borderColor: ["rgba(16,185,129,1)", "rgba(239,68,68,1)"], borderWidth: 1 }],
        };
      } else {
        chartData = {
          labels: ["Pending Amount", "Accepted Amount", "Rejected Amount"],
          datasets: [{ data: [graphData.pendingAmount || 0, graphData.acceptedAmount || 0, graphData.rejectedAmount || 0], backgroundColor: ["rgba(245,158,11,0.75)", "rgba(16,185,129,0.75)", "rgba(239,68,68,0.75)"], borderColor: ["rgba(245,158,11,1)", "rgba(16,185,129,1)", "rgba(239,68,68,1)"], borderWidth: 1 }],
        };
      }
      options.scales = {};
      options.plugins.tooltip.callbacks.label = function (context) {
        let lbl = context.label || "";
        if (lbl) lbl += ": ";
        const val = context.raw;
        if (val !== null) lbl += "$" + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return lbl;
      };
    } else {
      options.scales = {
        x: { stacked: chartType === "stackedBar", grid: { display: false }, ticks: { color: "#9ca3af", font: { family: "Inter", size: 11 } } },
        y: { stacked: chartType === "stackedBar", grid: { color: "#f3f4f6" }, ticks: { color: "#9ca3af", font: { family: "Inter", size: 11 }, callback: (val) => "$" + val.toLocaleString() } },
      };

      let datasets = [];
      if (type === "invoice") {
        datasets = [
          { label: "Paid Amount ($)", data: graphData.paidData || [], backgroundColor: chartType === "area" ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.75)", borderColor: "rgba(16,185,129,1)", borderWidth: 2, fill: chartType === "area", tension: 0.35, borderRadius: chartType === "bar" ? 4 : 0 },
          { label: "Due Amount ($)", data: graphData.dueData || [], backgroundColor: chartType === "area" ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.75)", borderColor: "rgba(239,68,68,1)", borderWidth: 2, fill: chartType === "area", tension: 0.35, borderRadius: chartType === "bar" ? 4 : 0 },
        ];
      } else {
        datasets = [
          { label: "Pending Amount ($)", data: graphData.pendingData || [], backgroundColor: chartType === "area" ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.75)", borderColor: "rgba(245,158,11,1)", borderWidth: 2, fill: chartType === "area", tension: 0.35, borderRadius: chartType === "bar" || chartType === "stackedBar" ? 4 : 0 },
          { label: "Accepted Amount ($)", data: graphData.acceptedData || [], backgroundColor: chartType === "area" ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.75)", borderColor: "rgba(16,185,129,1)", borderWidth: 2, fill: chartType === "area", tension: 0.35, borderRadius: chartType === "bar" || chartType === "stackedBar" ? 4 : 0 },
          { label: "Rejected Amount ($)", data: graphData.rejectedData || [], backgroundColor: chartType === "area" ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.75)", borderColor: "rgba(239,68,68,1)", borderWidth: 2, fill: chartType === "area", tension: 0.35, borderRadius: chartType === "bar" || chartType === "stackedBar" ? 4 : 0 },
        ];
      }
      chartData = { labels, datasets };
    }

    const mappedType = isPieOrDoughnut ? chartType : chartType === "stackedBar" ? "bar" : chartType === "area" ? "line" : chartType;
    chartInstanceRef.current = new Chart(ctx, { type: mappedType, data: chartData, options });

    return () => { if (chartInstanceRef.current) chartInstanceRef.current.destroy(); };
  }, [graphData, chartType]);

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i);

  const totalCount = type === "invoice" ? (graphData?.totalInvoices || 0) : (graphData?.totalQuotes || 0);
  const totalAmount = graphData?.totalAmount || 0;
  const totalTax = graphData?.totalTax || 0;

  return (
<div className="relative bg-white rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 flex flex-col transition-all hover:shadow-md">

  {/* Header */}
  <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
        {icon}
      </div>

      <div>
        <h4 className="text-lg xl:text-2xl font-bold text-gray-800 tracking-tight">
          {title}
        </h4>

        <p className="text-xs text-gray-600">
          Dynamic QuickBooks analytics report
        </p>
      </div>
    </div>

    <div className="flex items-center gap-2">
      <label className="text-xs text-gray-600">
        Year:
      </label>

      <select
        value={year}
        onChange={(e) => setYear(Number(e.target.value))}
        className="text-xs border border-gray-200 rounded-xl px-3 py-1 bg-white text-gray-600 font-bold focus:ring-0 focus:ring-[#115B9D] outline-none cursor-pointer hover:bg-gray-100 transition-colors"
      >
        {availableYears.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  </div>

  {/* Metrics */}
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
    {[
      {
        label: "Total Count",
        value: totalCount,
        format: (v) => v,
        color: "text-[#115B9D]",
      },
      {
        label: "Total Amount",
        value: totalAmount,
        format: (v) =>
          `$${v.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        color: "text-[#fbbf24]",
      },
      {
        label: "Total Tax",
        value: totalTax,
        format: (v) =>
          `$${v.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        color: "text-[#2d8236]",
      },
    ].map(({ label, value, format, color }, i) => {
      const bgClasses = [
        "bg-[#115b9d12]",
        "bg-[#fbbf2414]",
        "bg-[#2d823617]",
      ];

      const borderColors = [
        "#115B9D",
        "#fbbf24",
        "#2d8236",
      ];

      return (
        <div
          key={label}
          className="text-center border border-l-transparent rounded-3xl border-l-[3px]"
          style={{
            background: `linear-gradient(white, white) padding-box,
            linear-gradient(180deg, #fff, ${borderColors[i]}, #fff) border-box`,
          }}
        >
          <div
            className={`${bgClasses[i]} flex flex-col justify-center items-center w-full h-full rounded-[22px] px-3 py-6`}
          >
            <p className={`text-sm font-extrabold ${color}`}>
              {format(value)}
            </p>

            <p className="text-xs sm:text-[13px] font-bold text-gray-600 uppercase tracking-wider">
              {label}
            </p>
          </div>
        </div>
      );
    })}
  </div>

  {/* Chart Type Buttons */}
  <div className="max-sm:w-full flex overflow-auto items-center gap-x-4 gap-y-2 mb-6 bg-white p-1.5 rounded-xl border self-end max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden px-3 py-2.5">

    {["bar", "line", "area"].map((cType) => (
      <button
        key={cType}
        onClick={() => setChartType(cType)}
        className={`text-sm transition-all border-b-[3px] border-transparent whitespace-nowrap ${
          chartType === cType
            ? "border-b-[2px] !border-[#115B9D] shadow-sm"
            : "text-gray-500 hover:text-gray-800"
        }`}
      >
        {cType.charAt(0).toUpperCase() + cType.slice(1)}
      </button>
    ))}

    {type === "estimate" && (
      <button
        onClick={() => setChartType("stackedBar")}
        className={`text-sm transition-all border-b-[3px] border-transparent whitespace-nowrap ${
          chartType === "stackedBar"
            ? "border-b-[2px] !border-[#115B9D] shadow-sm"
            : "text-gray-500 hover:text-gray-800"
        }`}
      >
        Stacked Bar
      </button>
    )}

    {["pie", "doughnut"].map((cType) => (
      <button
        key={cType}
        onClick={() => setChartType(cType)}
        className={`text-sm transition-all border-b-[3px] border-transparent whitespace-nowrap ${
          chartType === cType
            ? "border-b-[2px] !border-[#115B9D] shadow-sm"
            : "text-gray-500 hover:text-gray-800"
        }`}
      >
        {cType.charAt(0).toUpperCase() + cType.slice(1)}
      </button>
    ))}
  </div>

  {/* Chart Area */}
  <div className="relative flex-grow min-h-0 w-full bg-[#F8FAFC] border border-gray-200 rounded-xl p-3">
    {graphData ? (
      <canvas ref={canvasRef} />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
        <p className="text-sm text-gray-600 font-medium">
          No Data Available
        </p>
      </div>
    )}
  </div>

</div>
  );
};

// ─── Main Dashboard Export ─────────────────────────────────────────────────
export default function Dashboard() {
  const Navigate = useNavigate();
  const [countData, setCountData] = useState({});
  const [invoiceYear, setInvoiceYear] = useState(2026);
  const [estimateYear, setEstimateYear] = useState(2026);
  const [invoiceGraphData, setInvoiceGraphData] = useState(null);
  const [estimateGraphData, setEstimateGraphData] = useState(null);

  const getCountdata = (year) => {
    loader(true);
    ApiClient.get("quickbooks-docs/user-dashboard-data", { year }).then((res) => {
      if (res?.success) setCountData(res?.data);
      loader(false);
    });
  };

  const getInvoiceGraphData = (year) => {
    loader(true);
    ApiClient.get("quickbooks-docs/invoice-graph", { year }).then((res) => {
      if (res?.success) setInvoiceGraphData(res?.data);
      loader(false);
    });
  };

  const getEstimateGraphData = (year) => {
    loader(true);
    ApiClient.get("quickbooks-docs/estimate-graph", { year }).then((res) => {
      if (res?.success) setEstimateGraphData(res?.data);
      loader(false);
    });
  };

  useEffect(() => {
    getCountdata(estimateYear);
    getEstimateGraphData(estimateYear);
  }, [estimateYear]);

  useEffect(() => {
    getInvoiceGraphData(invoiceYear);
  }, [invoiceYear]);

  const tabs = countData?.estimates?.tabs || {};
  const invoices = countData?.invoices || {};
  const estimates = countData?.estimates || {};

  const total =
    (tabs.pending || 0) +
    (tabs.accepted || 0) +
    (tabs.rejected || 0) +
    (tabs.negotiation || 0) +
    (tabs.closed || 0);

  const estimateCards = [
    { title: "Total Estimates", subtitle: "All estimates", desc: "All estimate records across every status", value: total, Icon: FiFileText, iconColor: "#115B9D", iconBg: "#EEF4FF", onClick: () => Navigate("/orders?tab=estimate") },
    { title: "Pending", subtitle: "Awaiting action", desc: "Estimates sent but not yet reviewed", value: tabs.pending || 0, Icon: FiClock, iconColor: "#F59E0B", iconBg: "#FFFBEB", onClick: () => Navigate("/orders?tab=estimate&subTab=pending") },
    { title: "Accepted", subtitle: "Approved", desc: "Estimates confirmed and moved forward", value: tabs.accepted || 0, Icon: FiCheckCircle, iconColor: "#10B981", iconBg: "#ECFDF5", onClick: () => Navigate("/orders?tab=estimate&subTab=synced") },
    { title: "Rejected", subtitle: "Declined", desc: "Estimates declined by the client", value: tabs.rejected || 0, Icon: FiXCircle, iconColor: "#EF4444", iconBg: "#FEF2F2", onClick: () => Navigate("/orders?tab=estimate&subTab=rejected") },
    { title: "Negotiation", subtitle: "In discussion", desc: "Estimates currently under active revision", value: tabs.negotiation || 0, Icon: FiMessageSquare, iconColor: "#8B5CF6", iconBg: "#F5F3FF", onClick: () => Navigate("/orders?tab=estimate&subTab=negotiate") },
    { title: "Closed", subtitle: "Finalized", desc: "Estimates that have been fully closed out", value: tabs.closed || 0, Icon: FiLock, iconColor: "#6B7280", iconBg: "#F9FAFB", onClick: () => Navigate("/orders?tab=estimate&subTab=closed") },
  ];

  const invoiceCards = [
    { title: "Total Invoices", subtitle: "All invoices", desc: "Complete count of all generated invoices", value: invoices.totalInvoices || 0, Icon: FiFile, iconColor: "#115B9D", iconBg: "#EEF4FF", onClick: () => Navigate("/orders?tab=invoice") },
    { title: "Paid Invoices", subtitle: "Settled", desc: "Invoices fully settled by the client", value: invoices.paidInvoices || 0, Icon: FiCreditCard, iconColor: "#10B981", iconBg: "#ECFDF5", onClick: () => Navigate("/orders?tab=invoice") },
    { title: "Due Invoices", subtitle: "Outstanding", desc: "Invoices with payment still pending", value: invoices.tabs?.due || 0, Icon: FiAlertCircle, iconColor: "#EF4444", iconBg: "#FEF2F2", onClick: () => Navigate("/orders?tab=invoice") },
    { title: "Amount Due", subtitle: "Balance owed", desc: "Total outstanding balance to be collected", value: `$${(invoices.amountDue || 0).toFixed(2)}`, Icon: FiDollarSign, iconColor: "#F59E0B", iconBg: "#FFFBEB", onClick: () => Navigate("/orders?tab=invoice") },
  ];

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] bg-[#FAFBFD]">
        <div className="container mx-auto px-5 pb-14 max-w-7xl">

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 cursor-pointer font-[300] pb-5 sm:py-8">
            <p onClick={() => Navigate("/")} className="text-sm text-[#414141] hover:opacity-80 transition-opacity">
              Home
            </p>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <p className="text-sm text-[#116DAE] font-[600]">Dashboard</p>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 tracking-tight">
              Quote & Invoice Analytics Dashboard
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Monitor, track, and analyze your QuickBooks quotes and invoices.
            </p>
          </div>

          <div className="space-y-12">

            {/* ── ESTIMATES SECTION ── */}
            <div>
              <SectionLabel delay={0}>Estimate Status</SectionLabel>
              <div className="grid max-[380px]:!grid-cols-1 grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
                {estimateCards.map((card, i) => (
                  <StatCard key={card.title} {...card} delay={150 + i * 70} />
                ))}
              </div>
            </div>

            {/* ── INVOICES SECTION ── */}
            <div>
              <SectionLabel delay={200}>Invoice Overview</SectionLabel>
              <div className="grid max-[380px]:!grid-cols-1 grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
                {invoiceCards.map((card, i) => (
                  <StatCard key={card.title} {...card} delay={600 + i * 70} />
                ))}
              </div>
            </div>

            {/* ── DIVIDER ── */}
            <div className="h-[1px] bg-gray-200" />

            {/* ── CHARTS SECTION ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <InteractiveChart
                title="Estimate Overview"
                type="estimate"
                graphData={estimateGraphData}
                year={estimateYear}
                setYear={setEstimateYear}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 26 26" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M18.4167 12.1875C21.8552 12.1875 24.6458 14.9782 24.6458 18.4167C24.6458 21.8552 21.8552 24.6458 18.4167 24.6458C14.9782 24.6458 12.1875 21.8552 12.1875 18.4167C12.1875 14.9782 14.9782 12.1875 18.4167 12.1875ZM17.6042 15.7083V18.4167C17.6042 18.8652 17.9682 19.2292 18.4167 19.2292H21.125C21.5735 19.2292 21.9375 18.8652 21.9375 18.4167C21.9375 17.9682 21.5735 17.6042 21.125 17.6042H19.2292V15.7083C19.2292 15.2598 18.8652 14.8958 18.4167 14.8958C17.9682 14.8958 17.6042 15.2598 17.6042 15.7083Z" fill="#115B9D" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.76953 2.70817V2.1665C6.76953 1.718 7.13353 1.354 7.58203 1.354C8.03053 1.354 8.39453 1.718 8.39453 2.1665V2.70817H6.76953Z" fill="#115B9D" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M17.6035 2.70817V2.1665C17.6035 1.718 17.9675 1.354 18.416 1.354C18.8645 1.354 19.2285 1.718 19.2285 2.1665V2.70817H17.6035Z" fill="#115B9D" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M13.5053 23.8333H4.33268C3.54293 23.8333 2.7846 23.5191 2.2256 22.9612C1.66768 22.4022 1.35352 21.6438 1.35352 20.8541V9.20825H24.6452V14.587C23.3571 12.4973 21.0485 11.1041 18.416 11.1041C14.3806 11.1041 11.1035 14.3812 11.1035 18.4166C11.1035 20.5627 12.0308 22.4953 13.5053 23.8333ZM1.35352 7.58325V5.68742C1.35352 4.89767 1.66768 4.13934 2.2256 3.58034C2.7846 3.02242 3.54293 2.70825 4.33268 2.70825H6.77018V4.87492C6.77018 5.32342 7.13418 5.68742 7.58268 5.68742C8.03118 5.68742 8.39518 5.32342 8.39518 4.87492V2.70825H17.6035V4.87492C17.6035 5.32342 17.9675 5.68742 18.416 5.68742C18.8645 5.68742 19.2285 5.32342 19.2285 4.87492V2.70825H21.666C22.4558 2.70825 23.2141 3.02242 23.7731 3.58034C24.331 4.13934 24.6452 4.89767 24.6452 5.68742V7.58325H1.35352Z" fill="#115B9D" />
                  </svg>
                }
              />
              <InteractiveChart
                title="Invoice Overview"
                type="invoice"
                graphData={invoiceGraphData}
                year={invoiceYear}
                setYear={setInvoiceYear}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 26 26" fill="none">
                    <path d="M21.8651 0H11.0773V7.3192C11.0773 7.80366 10.6845 8.1964 10.2001 8.1964H2.88086V24.7469C2.88086 25.4389 3.44194 26 4.13399 26H21.8651C22.5571 26 23.1182 25.4389 23.1182 24.7469V1.25313C23.1182 0.561082 22.5571 0 21.8651 0ZM16.9259 20.8083H9.78376C9.29931 20.8083 8.90656 20.4155 8.90656 19.9311C8.90656 19.4466 9.29931 19.0539 9.78376 19.0539H16.9259C17.4104 19.0539 17.8031 19.4466 17.8031 19.9311C17.8031 20.4155 17.4104 20.8083 16.9259 20.8083ZM16.9259 16.6817H9.78376C9.29931 16.6817 8.90656 16.289 8.90656 15.8045C8.90656 15.3201 9.29931 14.9274 9.78376 14.9274H16.9259C17.4104 14.9274 17.8031 15.3201 17.8031 15.8045C17.8031 16.289 17.4104 16.6817 16.9259 16.6817ZM16.9259 12.5553H9.78376C9.29931 12.5553 8.90656 12.1626 8.90656 11.6781C8.90656 11.1937 9.29931 10.8009 9.78376 10.8009H16.9259C17.4104 10.8009 17.8031 11.1937 17.8031 11.6781C17.8031 12.1626 17.4104 12.5553 16.9259 12.5553Z" fill="#115B9D" />
                    <path d="M9.3123 0C8.97999 0 8.66129 0.13198 8.42627 0.367098L3.24796 5.54521C3.01294 5.78023 2.88086 6.09893 2.88086 6.43124V6.44196H9.32297V0H9.3123Z" fill="#115B9D" />
                  </svg>
                }
              />
            </div>

          </div>
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}